import { type ReactNode, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { useAuthStore } from '@/stores/auth';
import {
  useAcknowledgeSanction,
  usePendingSanctions,
  type PendingSanction,
} from '@/modules/payments';
import { SanctionBody, SanctionFooter } from './sanction-popup-parts';

function eligible(status: string | undefined): boolean {
  if (!status) return false;
  return status !== 'banned' && status !== 'verifying' && status !== 'deleted';
}

type Queue = {
  current: PendingSanction | null;
  index: number;
  total: number;
  acknowledge: () => void;
  pending: boolean;
};

function useSanctionQueue(): Queue {
  const status = useAuthStore((s) => s.user?.account_status);
  const query = usePendingSanctions(eligible(status));
  const items = query.data?.items ?? [];
  const ack = useAcknowledgeSanction();
  const [seen, setSeen] = useState<string[]>([]);
  const visible = items.filter((i) => !seen.includes(i.id));
  const current = visible[0] ?? null;
  const acknowledge = (): void => {
    if (!current) return;
    const id = current.id;
    ack.mutate(id, { onSuccess: () => setSeen((prev) => [...prev, id]) });
  };
  return {
    current,
    index: items.length - visible.length,
    total: items.length,
    pending: ack.isPending,
    acknowledge,
  };
}

export function SanctionAcknowledgePopup(): ReactNode {
  const q = useSanctionQueue();
  if (!q.current) return null;
  return (
    <Modal
      open
      onClose={() => {}}
      title="Has recibido una sanción"
      size="sm"
      dismissible={false}
    >
      <div className="space-y-base p-base">
        <SanctionBody s={q.current} />
        <SanctionFooter
          index={q.index}
          total={q.total}
          pending={q.pending}
          onAck={q.acknowledge}
        />
      </div>
    </Modal>
  );
}
